import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Package, BookOpen, ShoppingBag, User, Wallet, History, Play, Newspaper, TrendingUp, GitBranch, Users, Crown, CreditCard, IdCard, Share2, Store, QrCode, Bell, Trophy, Download, Headphones, Link2, Gavel, Tag, Building, MapPin, Ticket, Wrench, MessageCircle, FileText, Star, Gamepad2, Coins, Camera, Video, Wand2, Sparkles, ArrowRight, ChevronDown, ChevronUp, ExternalLink, Heart, ShieldCheck, Landmark, Briefcase } from 'lucide-react';
import api from '../../utils/api';
import CommunityStats from '../../components/CommunityStats';
import ReferralLeaderboard from '../../components/ReferralLeaderboard';
import AttractionPopup from '../../components/AttractionPopup';
import AVXMiniWidget from '../../components/AVXMiniWidget';
import DailyTarotWidget from '../../components/DailyTarotWidget';
import ReferralShareCard from '../../components/ReferralShareCard';
import ReferralCouponCard from '../../components/ReferralCouponCard';
import AVXTodayPnLWidget from '../../components/AVXTodayPnLWidget';
import TSBMiniWidget from '../../components/TSBMiniWidget';
import ActivationBanner from '../../components/ActivationBanner';
import SamadhanJourneyWidget from '../../components/SamadhanJourneyWidget';
import QuickTransfer from '../../components/QuickTransfer';
import CustomDashboardTabs from '../../components/CustomDashboardTabs';
import ShareButton from '../../components/ShareButton';
import UnlockedProducts from '../../components/UnlockedProducts';
import SoundboxWidget from '../../components/soundbox/SoundboxWidget'; // kept for potential local use
import ShareEarningsCounter from '../../components/ShareEarningsCounter';
import HotNewsMarquee from '../../components/HotNewsMarquee';
import CommissionDashboard from '../../components/CommissionDashboard';
import LiteModeToggle from '../../components/LiteModeToggle';
import BuyATBalanceModal from '../../components/BuyATBalanceModal';
import CityProHeroWidget from '../../components/CityProHeroWidget';
import MarketplaceHeroWidget from '../../components/MarketplaceHeroWidget';
import MarketplaceCategoriesWidget from '../../components/MarketplaceCategoriesWidget';
import MyBookingsWidget from '../../components/MyBookingsWidget';
import { useHiddenTools } from '../../hooks/useHiddenTools';

// Hidden-tool links are hidden site-wide by components/HiddenToolsStyleInjector
// (mounted in Layout); widgets below that aren't plain links use isHidden(slug).

const UserDashboard = () => {
  const { user } = useAuth();
  // Support fix 2026-09-24: widgets must also disappear when admin hides their tool
  const { isHidden } = useHiddenTools();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ totalOrders: 0, totalSpent: 0 });
  const [displayWindows, setDisplayWindows] = useState([]);
  const [showCommissionDashboard, setShowCommissionDashboard] = useState(false);
  const [headerLinks, setHeaderLinks] = useState([]);
  const [showBuyAT, setShowBuyAT] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchDisplayWindows();
    fetchHeaderLinks();
  }, []);

  const fetchHeaderLinks = async () => {
    try {
      const response = await api.get('/important-links?position=header');
      setHeaderLinks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch header links:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
      
      const completed = response.data.filter(o => o.payment_status === 'completed');
      const totalSpent = completed.reduce((sum, o) => sum + o.total_amount, 0);
      
      setStats({
        totalOrders: response.data.length,
        totalSpent
      });
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const fetchDisplayWindows = async () => {
    try {
      const response = await api.get('/display-windows');
      setDisplayWindows(response.data);
    } catch (error) {
      console.error('Failed to fetch display windows:', error);
    }
  };

  // Extract YouTube embed URL
  const getEmbedUrl = (url) => {
    if (!url) return '';
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    return url;
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="user-dashboard">
      {/* Redeem Activation Key — quick access widget for logged-in users */}
      <div className="max-w-6xl mx-auto mb-4">
        <a href="/redeem-key" data-testid="dash-redeem-key-cta"
           className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-amber-500/30 via-yellow-500/30 to-orange-500/30 border border-yellow-400/40 p-3 hover:from-amber-500/50 hover:to-orange-500/50 transition">
          <div className="text-2xl">🎁</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black text-yellow-200">Redeem Activation Key</div>
            <div className="text-[10px] opacity-80">Have a coupon or activation code? Unlock a tool instantly</div>
          </div>
          <div className="text-xs font-black bg-yellow-400 text-slate-900 px-3 py-1.5 rounded-lg">Redeem →</div>
        </a>
      </div>

      {/* My Bookings — appointments I've booked at various shops */}
      <MyBookingsWidget />
      <div className="max-w-7xl mx-auto">
        {/* 🛒 ANANT SMART MARKETPLACE — Position #1 hero (Blueprint v6) */}
        <div className="mb-4 -mx-6">
          {!isHidden('marketplace') && <MarketplaceHeroWidget />}
          {!isHidden('marketplace') && <MarketplaceCategoriesWidget />}
        </div>

        {/* 🏙️ CITYPRO CONNECT — TOP HERO (Feb 2026) */}
        <div className="mb-6 -mx-6">
          {!isHidden('citypro') && <CityProHeroWidget />}
        </div>

        {/* HOT NEWS MARQUEE - SCROLLING ANNOUNCEMENTS */}
        <HotNewsMarquee className="mb-6 -mx-6 px-6" />

        {/* ACTIVATION BANNER - TOP PRIORITY */}
        <div className="mb-8">
          <ActivationBanner />
        </div>

        <div className="mb-8">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
            <h1 className="text-4xl md:text-5xl font-bold" data-testid="dashboard-title">
              Welcome back, {user?.name}!
            </h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowBuyAT(true)}
                data-testid="dashboard-buy-at-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-black hover:scale-105 transition"
              >
                <Sparkles className="w-3.5 h-3.5" /> Buy AT Balance
              </button>
              <LiteModeToggle />
            </div>
          </div>
          <p className="text-muted-foreground">Manage your orders and access your purchases</p>
          <p className="text-xs text-amber-300/70 mt-1">
            📱 अगर mobile screen crash/glitch हो → ऊपर "Lite Mode" toggle दबाइए
          </p>
        </div>

        {/* Daily Tarot Card Widget — viral 1-tap WhatsApp share */}
        <div className="mb-6">
          {!isHidden('anant-tarot') && <DailyTarotWidget />}
        </div>

        {/* 🆕 ANANT FEELINGS — Direct Dil Se · AI Companion */}
        <Link
          to="/feelings"
          data-testid="dashboard-feelings-shortcut"
          className="block mb-4 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-rose-400/50 bg-gradient-to-br from-rose-600/30 via-pink-600/25 to-purple-700/30 p-5 sm:p-6 hover:border-amber-300 hover:scale-[1.01] transition-all">
            <div className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-extrabold rounded-full bg-rose-500 text-white uppercase tracking-wider">NEW</div>
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-rose-400 via-pink-400 to-purple-500 flex items-center justify-center shadow-lg shadow-rose-500/30 group-hover:scale-110 transition">
                <Heart className="w-7 h-7 sm:w-8 sm:h-8 text-white" fill="white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-400/20 text-rose-200 border border-rose-400/30">Direct Dil Se</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/30">200 AT · 10 min</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold text-amber-200 leading-tight">
                  ANANT FEELINGS — AI Companion
                </h3>
                <p className="text-xs sm:text-sm text-rose-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  Anushka · Anaya · Arjun · Voice + Text chat · Emotional support, fun, mentorship
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-amber-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Start</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 🆕 SANKAT MOCHAN — Paid 10-min VIP Consultation */}
        <Link
          to="/sankat/paid"
          data-testid="dashboard-sankat-paid-shortcut"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/60 bg-gradient-to-br from-amber-500/25 via-orange-500/20 to-rose-500/25 p-5 sm:p-6 hover:border-amber-300 hover:scale-[1.01] transition-all">
            <div className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-extrabold rounded-full bg-amber-500 text-black uppercase tracking-wider">VIP · NEW</div>
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-400 to-rose-500 flex items-center justify-center shadow-lg shadow-amber-500/40 group-hover:scale-110 transition">
                <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/30 text-amber-100 border border-amber-400/40">10-min Premium Session</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-400/20 text-rose-200 border border-rose-400/30">200 AT</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold text-amber-200 leading-tight">
                  SANKAT MOCHAN · VIP Consultation
                </h3>
                <p className="text-xs sm:text-sm text-amber-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  1-on-1 with your chosen Guru avatar · Personalized tips, solutions, emotional support
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-amber-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Start</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 🆕 REFERRAL SHARE CARD — viral acquisition card (Instagram-story sized PNG) */}
        <ReferralShareCard user={user} />

        {/* 🎁 REFERRAL COUPON CODE — WhatsApp status shareable, +10 AT both sides */}
        <div className="mb-6">
          <ReferralCouponCard />
        </div>

        {/* TAROT GALLERY — Public viral shareable gallery of 79 cards */}
        <Link
          to="/tarot/gallery"
          data-testid="tarot-gallery-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/40 bg-slate-900 p-5 sm:p-6 hover:border-amber-300 transition">
            <div className="flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-400 to-rose-500 flex items-center justify-center group-hover:scale-110 transition">
                <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/30">SHARE & EARN</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-200 border border-emerald-400/30">79 ARTWORKS</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-amber-300 via-orange-300 to-rose-300 bg-clip-text text-transparent leading-tight">
                  TAROT GALLERY — Share with the World
                </h3>
                <p className="text-xs sm:text-sm text-amber-100/70 mt-0.5 sm:mt-1 line-clamp-2">
                  Beautiful AI-crafted Osho-Zen cards · 1-tap WhatsApp share · SEO-ready public link · Viral your portal
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-amber-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">View</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* ANANT INNER SELF GUIDANCE — Tarot shortcut */}
        <Link
          to="/anant-tarot"
          data-testid="anant-tarot-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-violet-950 via-fuchsia-950 to-indigo-950 p-5 sm:p-6 hover:border-fuchsia-300 hover:shadow-2xl hover:shadow-fuchsia-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-fuchsia-400 via-violet-500 to-amber-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/40 group-hover:scale-110 transition">
                <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/20 text-fuchsia-200 border border-fuchsia-400/30">NEW · SPIRITUAL</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/30">Free Daily Card · GPT-4o</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-fuchsia-300 via-violet-300 to-amber-300 bg-clip-text text-transparent leading-tight">
                  ANANT INNER SELF GUIDANCE
                </h3>
                <p className="text-xs sm:text-sm text-violet-200/70 mt-0.5 sm:mt-1 line-clamp-2">
                  Osho Zen Tarot · 79 cards · AI-powered readings · 7-Level Network Rewards · Anant Token (1 AT = ₹1)
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-fuchsia-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Begin</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* ANANT AI TRIAL ROOM — Virtual Try-On (moved above Samadhan) */}
        <Link
          to="/trial-room"
          data-testid="trial-room-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-cyan-400/40 bg-gradient-to-br from-slate-950 via-cyan-950/70 to-emerald-950/50 p-5 sm:p-6 hover:border-cyan-300 hover:shadow-2xl hover:shadow-cyan-500/30 transition-all duration-300">
            <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(circle at 20% 30%, rgba(6,182,212,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(236,72,153,0.35) 0%, transparent 40%)' }} />
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-cyan-400 via-fuchsia-500 to-amber-500 flex items-center justify-center shadow-lg shadow-cyan-500/50 group-hover:scale-110 group-hover:rotate-3 transition text-3xl">
                🪞
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-400/20 text-cyan-300 border border-cyan-400/40 animate-pulse">🚀 NEW · AI VISION</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/30">25 AT / TRIAL · MIN 2</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">🏷️ OWN BRANDING</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-amber-200 bg-clip-text text-transparent leading-tight">
                  ANANT AI TRIAL ROOM
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-0.5 sm:mt-1 line-clamp-2">
                  Virtual try-on · Har image pe aap ka <b className="text-amber-300">shop logo &amp; naam</b> auto-watermark · Pay-per-trial 25 AT (min 2) · Privacy-first
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-cyan-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Try Now</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 📣 ANANT SMART MARKETING (ASM) — Digital Business Card + WhatsApp Mini-Store */}
        <Link
          to="/asm"
          data-testid="asm-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-fuchsia-400/50 bg-gradient-to-br from-fuchsia-950 via-pink-900 to-cyan-950 p-5 sm:p-6 hover:border-pink-300 hover:shadow-2xl hover:shadow-fuchsia-500/40 transition-all duration-300">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-fuchsia-500/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-cyan-500/25 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500 via-pink-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-fuchsia-500/50 group-hover:scale-110 group-hover:rotate-3 transition text-3xl">
                📣
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/25 text-fuchsia-200 border border-fuchsia-400/40 animate-pulse">🚀 NEW · VIRAL CARD</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/25 text-amber-200 border border-amber-400/30">Pro: 600 AT/mo · 6000 AT/yr</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/25 text-emerald-200 border border-emerald-400/30">7-LEVEL Cascade</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-fuchsia-300 via-pink-200 to-cyan-300 bg-clip-text text-transparent leading-tight">
                  ANANT SMART MARKETING
                </h3>
                <p className="text-xs sm:text-sm text-fuchsia-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  Digital business card · Voice greeter · Festive banners · WhatsApp mini-store · Auto-share · Starter FREE
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-pink-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 📱 ANANT VYAPAAR MASTER — WhatsApp Marketing Suite (600 AT/mo · 3-Level Cashback) */}
        <Link
          to="/vyapaar-master"
          data-testid="avm-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-950 via-teal-900 to-cyan-950 p-5 sm:p-6 hover:border-emerald-300 hover:shadow-2xl hover:shadow-emerald-500/40 transition-all duration-300">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-cyan-500/25 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-emerald-500/50 group-hover:scale-110 group-hover:rotate-3 transition text-3xl">
                📱
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/25 text-emerald-200 border border-emerald-400/40 animate-pulse">🚀 NEW · WHATSAPP SUITE</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/25 text-amber-200 border border-amber-400/30">600 AT/mo · 6000 AT/yr</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/25 text-fuchsia-200 border border-fuchsia-400/30">3-Level: 15% · 8% · 5%</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-300 bg-clip-text text-transparent leading-tight">
                  ANANT VYAPAAR MASTER
                </h3>
                <p className="text-xs sm:text-sm text-emerald-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  12 ready WhatsApp templates · Custom AI rewrite · Customer groups + bulk sequential sender · QR shop card · 0 server cost
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-emerald-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 🎨 ANANT SMART FLYER SERVICES — Flyer/Pamphlet creator (300 AT/mo · 22 Templates) */}
        <Link
          to="/flyer-studio"
          data-testid="flyer-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-orange-400/50 bg-gradient-to-br from-orange-950 via-amber-900 to-yellow-950 p-5 sm:p-6 hover:border-orange-300 hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-orange-500/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-yellow-500/25 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-400 flex items-center justify-center shadow-lg shadow-orange-500/50 group-hover:scale-110 group-hover:rotate-3 transition text-3xl">
                🎨
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-400/25 text-orange-200 border border-orange-400/40 animate-pulse">🎨 NEW · FLYER STUDIO</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/25 text-amber-200 border border-amber-400/30">300 AT/mo · 3000 AT/yr</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/25 text-fuchsia-200 border border-fuchsia-400/30">AI · Marketplace</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-orange-300 via-amber-200 to-yellow-300 bg-clip-text text-transparent leading-tight">
                  ANANT SMART FLYER SERVICES
                </h3>
                <p className="text-xs sm:text-sm text-orange-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  22 templates · 7 formats (A4 / WA / Insta / FB / YT) · AI generator (20 AT) · Public share · Community marketplace (buy/sell 20 AT)
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-orange-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 🛒 ANANT SMART MARKETPLACE shortcut card */}
        <Link
          to="/marketplace"
          data-testid="marketplace-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-purple-400/50 p-5 sm:p-6 hover:border-purple-300 hover:shadow-2xl hover:shadow-purple-500/40 transition-all duration-300"
               style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #4338CA 40%, #7C3AED 70%, #DB2777 100%)' }}>
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-fuchsia-500/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-yellow-400 via-pink-400 to-purple-500 flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition text-3xl">
                🛒
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-400 text-slate-900 border border-yellow-300 animate-pulse">⭐ FEATURED CORE ENGINE</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/25 text-fuchsia-100 border border-fuchsia-400/40">100+ LIVE SHOPPERS</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold text-white leading-tight">
                  ANANT SMART MARKETPLACE
                </h3>
                <p className="text-xs sm:text-sm text-white/80 mt-0.5 sm:mt-1 line-clamp-2">
                  Products · Services · Showrooms · 300/600/1500 AT plans · Guest browsable · 1-click WA leads
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-white group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 🏬 ANANT TRADE CENTER (ATC) — reverse-marketplace bidding hub */}
        {/* Wave 31 · Cinematic ATC B2C + B2B split shortcuts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="atc-shortcut-split">
          <Link to="/atc/b2c" data-testid="atc-shortcut-card-b2c" className="block group">
            <div className="relative overflow-hidden rounded-2xl border-2 border-cyan-400/60 p-4 sm:p-5 h-full hover:border-cyan-300 hover:shadow-2xl hover:shadow-cyan-500/40 transition-all duration-300"
                 style={{ background: 'linear-gradient(135deg, #082F49 0%, #0891B2 40%, #06B6D4 75%, #0F766E 100%)' }}>
              <div className="absolute -top-14 -right-14 w-52 h-52 rounded-full bg-cyan-400/30 blur-3xl pointer-events-none animate-pulse"></div>
              <div className="absolute -bottom-14 -left-14 w-52 h-52 rounded-full bg-emerald-400/25 blur-3xl pointer-events-none"></div>
              <div className="relative flex items-center gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-300 via-cyan-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-cyan-500/50 group-hover:scale-110 group-hover:rotate-3 transition text-2xl">
                  🏬
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-300 text-slate-900 animate-pulse">🔴 LIVE</span>
                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/20 text-white">B2C · Retail</span>
                  </div>
                  <h3 className="text-base sm:text-lg font-extrabold text-white leading-tight">ATC Trade Floor · B2C</h3>
                  <p className="text-[11px] text-white/85 mt-0.5 line-clamp-2">Reverse retail bidding · 4-digit OTP escrow · 5% platform · voice pitch bids</p>
                </div>
                <div className="flex-shrink-0 hidden sm:block text-white group-hover:translate-x-1 transition">
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </div>
          </Link>
          <Link to="/atc/b2b" data-testid="atc-shortcut-card-b2b" className="block group">
            <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/60 p-4 sm:p-5 h-full hover:border-amber-300 hover:shadow-2xl hover:shadow-amber-500/40 transition-all duration-300"
                 style={{ background: 'linear-gradient(135deg, #431407 0%, #B45309 40%, #F59E0B 75%, #DC2626 100%)' }}>
              <div className="absolute -top-14 -right-14 w-52 h-52 rounded-full bg-amber-400/30 blur-3xl pointer-events-none animate-pulse"></div>
              <div className="absolute -bottom-14 -left-14 w-52 h-52 rounded-full bg-rose-500/25 blur-3xl pointer-events-none"></div>
              <div className="relative flex items-center gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-300 via-orange-500 to-rose-600 flex items-center justify-center shadow-lg shadow-amber-500/50 group-hover:scale-110 group-hover:rotate-3 transition text-2xl">
                  🏭
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-300 text-slate-900 animate-pulse">🔴 LIVE</span>
                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/20 text-white">B2B · Bulk</span>
                  </div>
                  <h3 className="text-base sm:text-lg font-extrabold text-white leading-tight">ATC Trade Floor · B2B</h3>
                  <p className="text-[11px] text-white/85 mt-0.5 line-clamp-2">Bulk demand · MOQ enforced · 2-stage sample escrow · 3% platform · large orders</p>
                </div>
                <div className="flex-shrink-0 hidden sm:block text-white group-hover:translate-x-1 transition">
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </div>
          </Link>
        </div>

        <Link
          to="/atc"
          data-testid="atc-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/60 p-5 sm:p-6 hover:border-amber-300 hover:shadow-2xl hover:shadow-amber-500/40 transition-all duration-300"
               style={{ background: 'linear-gradient(135deg, #0F172A 0%, #0F766E 35%, #B45309 75%, #DC2626 100%)' }}>
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-400/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-emerald-500/25 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 flex items-center justify-center shadow-lg shadow-amber-500/50 group-hover:scale-110 group-hover:rotate-3 transition text-3xl">
                🏬
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400 text-slate-900 border border-amber-300 animate-pulse">🔥 NEW · REVERSE MARKET</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/25 text-emerald-100 border border-emerald-400/40">LIVE VOICE BROADCAST</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold text-white leading-tight">
                  ANANT TRADE CENTER · ATC
                </h3>
                <p className="text-xs sm:text-sm text-white/85 mt-0.5 sm:mt-1 line-clamp-2">
                  Post karo demand — merchants bid karenge · Lowest wins · 4-digit OTP escrow · 1% share cashback · B2C 5% / B2B 3%
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-white group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Trade</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>
        <Link
          to="/webshop"
          data-testid="webshop-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-950 via-teal-900 to-slate-950 p-5 sm:p-6 hover:border-emerald-300 hover:shadow-2xl hover:shadow-emerald-500/40 transition-all duration-300">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-teal-500/25 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-amber-500 flex items-center justify-center shadow-lg shadow-emerald-500/50 group-hover:scale-110 group-hover:rotate-3 transition text-3xl">
                🏪
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/25 text-emerald-200 border border-emerald-400/40 animate-pulse">🏪 NEW · WEBSHOP</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/25 text-amber-200 border border-amber-400/30">3-DAY FREE TRIAL</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-400/25 text-teal-200 border border-teal-400/30">600 AT/mo · 6000 AT/yr</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-emerald-300 via-teal-200 to-amber-300 bg-clip-text text-transparent leading-tight">
                  ANANT SMART WEBSHOP
                </h3>
                <p className="text-xs sm:text-sm text-emerald-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  B2C micro-shop · Products with AI descriptions (20 AT) · Public shop URL · Product share cards (canvas + WA share)
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-emerald-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 📣 ANANT SOCIAL SHARE — 0-cost image + audio + share suite (500 AT/mo · 5000 AT/yr) */}
        <Link
          to="/social-share"
          data-testid="social-share-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-fuchsia-400/50 bg-gradient-to-br from-fuchsia-950 via-pink-900 to-purple-950 p-5 sm:p-6 hover:border-fuchsia-300 hover:shadow-2xl hover:shadow-fuchsia-500/40 transition-all duration-300">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-fuchsia-500/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-pink-500/25 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500 via-pink-500 to-purple-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/50 group-hover:scale-110 group-hover:rotate-3 transition text-3xl">
                📣
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/25 text-fuchsia-200 border border-fuchsia-400/40 animate-pulse">📣 NEW · SOCIAL SHARE</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-pink-400/25 text-pink-200 border border-pink-400/30">500 AT/mo · 5000 AT/yr</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/25 text-amber-200 border border-amber-400/30">AI 20 AT · TTS · Native Share</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-fuchsia-300 via-pink-200 to-purple-300 bg-clip-text text-transparent leading-tight">
                  ANANT SOCIAL SHARE
                </h3>
                <p className="text-xs sm:text-sm text-fuchsia-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  Client-side canvas · Camera/Gallery/URL · Browser TTS + Voice · Product catalog · Native WA/Insta/Telegram share · Auto-watermark branding
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-fuchsia-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* SAMADHAN CENTER — The Dream Project shortcut */}
        <Link
          to="/samadhan"
          data-testid="samadhan-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-yellow-950 via-amber-950 to-orange-950 p-5 sm:p-6 hover:border-amber-300 hover:shadow-2xl hover:shadow-amber-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/40 group-hover:scale-110 transition text-3xl">
                🏛️
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/30 text-red-200 border border-red-400/40 animate-pulse">🚨 THE DREAM PROJECT</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/30">10 FOUNDER Seats · Lifetime</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300 bg-clip-text text-transparent leading-tight">
                  SAMADHAN CENTER
                </h3>
                <p className="text-xs sm:text-sm text-amber-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  Wellness · Entertainment · Leisure · 50 Cr AT Community Fund · Live 01/01/2031 · Certificate + Nameplate
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-amber-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Donate</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* MY SAMADHAN JOURNEY — personal donation summary */}
        {!isHidden('samadhan') && <SamadhanJourneyWidget />}

        {/* FREEDOM ACADEMY — Education hub shortcut */}
        <Link
          to="/academy"
          data-testid="freedom-academy-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-950 via-teal-950 to-slate-950 p-5 sm:p-6 hover:border-emerald-300 hover:shadow-2xl hover:shadow-emerald-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/40 group-hover:scale-110 transition">
                <BookOpen className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-200 border border-emerald-400/30">NEW · ACADEMY</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/30">19 Categories · AI Tutor</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-emerald-300 via-teal-300 to-amber-300 bg-clip-text text-transparent leading-tight">
                  FREEDOM ACADEMY
                </h3>
                <p className="text-xs sm:text-sm text-emerald-100/70 mt-0.5 sm:mt-1 line-clamp-2">
                  Trading · Spirituality · Business · Wellness · Network Marketing · 4 Pricing Tiers · Affiliate Network Rewards
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-emerald-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Learn</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 🎯 ANANT YOJANA MITRA — Government scheme discovery + Affiliate Network */}
        <Link
          to="/anant-yojana"
          data-testid="anant-yojana-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-950 via-teal-900 to-cyan-950 p-5 sm:p-6 hover:border-emerald-300 hover:shadow-2xl hover:shadow-emerald-500/40 transition-all duration-300">
            {/* Decorative glow */}
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-teal-500/20 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-400 flex items-center justify-center shadow-lg shadow-emerald-500/50 group-hover:scale-110 transition">
                <Landmark className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-200 border border-emerald-400/40">NEW · 48H FREE TRIAL</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/30">7-LEVEL Affiliate Network</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-400/20 text-cyan-200 border border-cyan-400/30">AI SCHEME MATCH</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-300 bg-clip-text text-transparent leading-tight">
                  ANANT YOJANA MITRA — Govt Schemes AI Discovery
                </h3>
                <p className="text-xs sm:text-sm text-emerald-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  अनंत योजना मित्र · PM-KISAN · Ayushman · PMAY · Mudra · Scholarships · Auto-match by profile · PDF Download
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-emerald-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 📰 ANANT SAMACHAR NETWORK — Hyperlocal News + Reporter Affiliate Network */}
        <Link
          to="/asn"
          data-testid="asn-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-blue-400/50 bg-gradient-to-br from-blue-950 via-indigo-900 to-amber-950 p-5 sm:p-6 hover:border-amber-300 hover:shadow-2xl hover:shadow-blue-500/40 transition-all duration-300">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-blue-500/25 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-amber-400 flex items-center justify-center shadow-lg shadow-blue-500/50 group-hover:scale-110 transition">
                <Newspaper className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/25 text-amber-200 border border-amber-400/40">NEW · AFFILIATE NETWORK</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-400/25 text-blue-200 border border-blue-400/30">DIGITAL ID CARD</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/25 text-emerald-200 border border-emerald-400/30">7-LEVEL · 33%</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-blue-300 via-amber-200 to-yellow-300 bg-clip-text text-transparent leading-tight" style={{ fontFamily: '"Cinzel", Georgia, serif' }}>
                  ANANT SAMACHAR NETWORK
                </h3>
                <p className="text-xs sm:text-sm text-blue-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  Be the Voice of Your City — Hyperlocal news + press ID + self-serve ads · 1000 AT activation
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-amber-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* ❤️ ANANT SMART HEALTH TRACKER — AI-powered health + 33% cascade */}
        <Link
          to="/health-tracker"
          data-testid="health-tracker-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-rose-400/50 bg-gradient-to-br from-rose-950 via-fuchsia-900 to-emerald-950 p-5 sm:p-6 hover:border-emerald-300 hover:shadow-2xl hover:shadow-rose-500/40 transition-all duration-300">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-emerald-500/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-rose-500/25 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-rose-500 via-fuchsia-500 to-emerald-400 flex items-center justify-center shadow-lg shadow-rose-500/50 group-hover:scale-110 transition">
                <Heart className="w-7 h-7 sm:w-8 sm:h-8 text-white animate-pulse" fill="white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-400/25 text-rose-200 border border-rose-400/40">NEW · CINEMATIC</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/25 text-emerald-200 border border-emerald-400/40">CAMERA PULSE · FOOD AI</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/25 text-fuchsia-200 border border-fuchsia-400/40">7-LEVEL · 33%</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-rose-300 via-fuchsia-200 to-emerald-300 bg-clip-text text-transparent leading-tight" style={{ fontFamily: '"Cinzel", Georgia, serif' }}>
                  ANANT SMART HEALTH TRACKER
                </h3>
                <p className="text-xs sm:text-sm text-rose-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  Camera PPG pulse · AI food scanner · BMI portal · Medical OCR locker · 500 AT/mo
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-emerald-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 💼 ANANT FIELDPRO AGENT — Offline-first PWA for field agents */}
        <Link
          to="/fieldpro"
          data-testid="fieldpro-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-violet-400/50 bg-gradient-to-br from-violet-950 via-indigo-900 to-cyan-950 p-5 sm:p-6 hover:border-cyan-300 hover:shadow-2xl hover:shadow-violet-500/40 transition-all duration-300">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-cyan-500/25 blur-3xl pointer-events-none animate-pulse"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-violet-500/25 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-violet-500 via-indigo-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-violet-500/50 group-hover:scale-110 transition">
                <Briefcase className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-400/25 text-violet-200 border border-violet-400/40">NEW · PWA</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-400/25 text-cyan-200 border border-cyan-400/40">OFFLINE-FIRST</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/25 text-emerald-200 border border-emerald-400/40">7-LEVEL · 33%</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-violet-300 via-indigo-200 to-cyan-300 bg-clip-text text-transparent leading-tight" style={{ fontFamily: '"Cinzel", Georgia, serif' }}>
                  ANANT FIELDPRO AGENT
                </h3>
                <p className="text-xs sm:text-sm text-violet-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  Pigmy · EMI · Insurance · B2B Orders · Virtual Soundbox · 300 AT/mo
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-cyan-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 📒 ANANT KHATA — Daily ledger for households & vendors */}
        <Link
          to="/anant-khata"
          data-testid="anant-khata-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950 via-orange-900 to-red-950 p-5 sm:p-6 hover:border-amber-300 hover:shadow-2xl hover:shadow-amber-500/40 transition-all duration-300">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-500/20 blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-orange-500/20 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-400 to-red-400 flex items-center justify-center shadow-lg shadow-amber-500/50 group-hover:scale-110 transition">
                <BookOpen className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/40">NEW · 30D FREE TRIAL</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-200 border border-emerald-400/30">50 AT REFERRAL</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-400/20 text-orange-200 border border-orange-400/30">GHAR + VYAPAR</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-amber-300 via-orange-200 to-yellow-300 bg-clip-text text-transparent leading-tight">
                  ANANT KHATA — Digital Daily Ledger
                </h3>
                <p className="text-xs sm:text-sm text-amber-100/80 mt-0.5 sm:mt-1 line-clamp-2">
                  अनंत खाता · Milkman · Tiffin · Laundry · Ghar ka kharcha · Auto-sync vendors · WhatsApp bills
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-amber-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* ANANT SMART TRADING TOOLS & SERVICES — Premium shortcut */}
        <Link
          to="/algotrading"
          data-testid="anant-trading-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950 p-5 sm:p-6 hover:border-amber-300 hover:shadow-2xl hover:shadow-amber-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/40 group-hover:scale-110 transition">
                <TrendingUp className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">PREMIUM · NEW</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/30">AI Signals · Auto Bots · F&O</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-amber-300 via-orange-400 to-pink-400 bg-clip-text text-transparent leading-tight">
                  ANANT SMART TRADING TOOLS &amp; SERVICES
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-0.5 sm:mt-1 line-clamp-2">
                  9 algorithmic strategies · Paper &amp; Live trading · AI Smart Signals · Stocks · Crypto · Forex · F&amp;O
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-amber-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* ANANT VYAPAR — CRM & Billing for local businesses */}
        <Link
          to="/vyapar"
          data-testid="anant-vyapar-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-slate-950 via-fuchsia-950 to-purple-950 p-5 sm:p-6 hover:border-fuchsia-300 hover:shadow-2xl hover:shadow-fuchsia-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/40 group-hover:scale-110 transition">
                <Store className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/30">7-DAY FREE TRIAL</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/20 text-fuchsia-300 border border-fuchsia-400/30">CRM · Billing · Udhari · Magic QR</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-fuchsia-300 via-pink-300 to-amber-300 bg-clip-text text-transparent leading-tight">
                  ANANT VYAPAR — Chota Business, Bada System
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-0.5 sm:mt-1 line-clamp-2">
                  Customer CRM · Sale Vouchers · Udhari Bahi · Magic QR · Defaulter Network · Free PDF/Excel Export
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-fuchsia-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* 🎨 ANANT LEADGEN — Lead Generation with Mini Landing Page */}
        <Link to="/leadgen" data-testid="leadgen-shortcut-card" className="block mb-6 group">
          <div className="relative overflow-hidden rounded-2xl border-2 border-cyan-400/40 bg-gradient-to-br from-slate-950 via-cyan-950 to-blue-950 p-5 sm:p-6 hover:border-cyan-300 hover:shadow-2xl hover:shadow-cyan-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/40 group-hover:scale-110 transition text-3xl">🎯</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/30">NEW · 300 AT/mo</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-400/20 text-cyan-300 border border-cyan-400/30">7-LEVEL Affiliate Network</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-cyan-300 via-blue-300 to-emerald-300 bg-clip-text text-transparent leading-tight">ANANT LEADGEN — Mini Landing Page + Smart Form</h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-0.5 sm:mt-1">Custom URL · QR Code · 7-field capture · Real-time stats · Leaderboard · Affiliate Network</p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-cyan-300 group-hover:translate-x-1 transition"><span className="text-sm font-semibold">Open</span><ArrowRight className="w-5 h-5" /></div>
            </div>
          </div>
        </Link>

        {/* 💰 ANANT CORPUS50 — 72-month SIP Crowdfund */}
        <Link to="/corpus50" data-testid="corpus50-shortcut-card" className="block mb-6 group">
          <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-slate-950 via-amber-950 to-orange-950 p-5 sm:p-6 hover:border-amber-300 hover:shadow-2xl hover:shadow-amber-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shadow-lg shadow-amber-500/40 group-hover:scale-110 transition text-3xl">💰</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/30">NEW · 72-MONTH S.I.P.</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30">50 CR AT TARGET</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300 bg-clip-text text-transparent leading-tight">ANANT CORPUS50 — Systematic Impact Pledge for Samadhan Center</h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-0.5 sm:mt-1">9 plans (100→75K AT/mo) · Nominee secure · Milestone badges · 7% referral · Cashback service</p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-amber-300 group-hover:translate-x-1 transition"><span className="text-sm font-semibold">Open</span><ArrowRight className="w-5 h-5" /></div>
            </div>
          </div>
        </Link>

        {/* 🔐 ANANT SMART VAULT — Encrypted browser vault */}
        <Link to="/vault" data-testid="vault-shortcut-card" className="block mb-6 group">
          <div className="relative overflow-hidden rounded-2xl border-2 border-fuchsia-400/40 bg-gradient-to-br from-slate-950 via-fuchsia-950 to-purple-950 p-5 sm:p-6 hover:border-fuchsia-300 hover:shadow-2xl hover:shadow-fuchsia-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/40 group-hover:scale-110 transition text-3xl">🔐</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/30">NEW · AES-256</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/20 text-fuchsia-300 border border-fuchsia-400/30">100% OFFLINE · 150 AT/mo</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-fuchsia-300 via-purple-300 to-pink-300 bg-clip-text text-transparent leading-tight">ANANT SMART VAULT — Crypto Seeds + Passwords</h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-0.5 sm:mt-1">Client-side AES-256 · Data never leaves browser · Passphrase protected · 7-level Affiliate Network</p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-fuchsia-300 group-hover:translate-x-1 transition"><span className="text-sm font-semibold">Open</span><ArrowRight className="w-5 h-5" /></div>
            </div>
          </div>
        </Link>

        {/* ANANT SAFETY QR — Emergency QR system */}
        <Link
          to="/safety-qr"
          data-testid="anant-safety-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-red-400/40 bg-gradient-to-br from-slate-950 via-red-950/60 to-orange-950/40 p-5 sm:p-6 hover:border-red-300 hover:shadow-2xl hover:shadow-red-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-red-500/40 group-hover:scale-110 transition">
                <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-400/20 text-red-300 border border-red-400/30">EMERGENCY · NEW</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-400/20 text-orange-300 border border-orange-400/30">Vehicle · Medical · Pet · Luggage</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-red-300 via-orange-300 to-amber-300 bg-clip-text text-transparent leading-tight">
                  ANANT SAFETY QR SYSTEM
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-0.5 sm:mt-1 line-clamp-2">
                  Har gaadi, har bag, har pet ke liye emergency QR · Scan pe location alert · Medical info · 7-Level Affiliate Network rewards
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-red-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* ANANT SYSTEM SAFETY TOOL — Browser antivirus + privacy */}
        <Link
          to="/asst"
          data-testid="asst-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-cyan-400/40 bg-gradient-to-br from-slate-950 via-cyan-950/60 to-fuchsia-950/40 p-5 sm:p-6 hover:border-cyan-300 hover:shadow-2xl hover:shadow-cyan-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-cyan-500/40 group-hover:scale-110 transition">
                <span className="text-2xl">🛡️</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-400/20 text-cyan-300 border border-cyan-400/30">SECURITY · NEW</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/20 text-fuchsia-300 border border-fuchsia-400/30">7-day Free Trial</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-cyan-300 via-teal-300 to-fuchsia-300 bg-clip-text text-transparent leading-tight">
                  ANANT SYSTEM SAFETY
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-0.5 sm:mt-1 line-clamp-2">
                  File malware scan · Privacy audit · Storage cleaner · Mobile secret codes · Security score 0-100
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-cyan-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* ANANT PORTFOLIO TRACKER — Personal finance module */}
        <Link
          to="/apts"
          data-testid="apts-shortcut-card"
          className="block mb-6 group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-indigo-400/40 bg-gradient-to-br from-slate-950 via-indigo-950/60 to-purple-950/40 p-5 sm:p-6 hover:border-indigo-300 hover:shadow-2xl hover:shadow-indigo-500/30 transition-all duration-300">
            <div className="relative flex items-center gap-4 sm:gap-5">
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-indigo-500/40 group-hover:scale-110 transition">
                <span className="text-2xl">💼</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-400/20 text-indigo-300 border border-indigo-400/30">FINANCE · NEW</span>
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-400/20 text-fuchsia-300 border border-fuchsia-400/30">7-day Free Trial</span>
                </div>
                <h3 className="text-base sm:text-2xl font-extrabold bg-gradient-to-r from-indigo-300 via-fuchsia-300 to-pink-300 bg-clip-text text-transparent leading-tight">
                  ANANT PORTFOLIO TRACKER
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 mt-0.5 sm:mt-1 line-clamp-2">
                  Net worth · Income/Expense ledger · Assets/Liabilities · EMI alerts · Goal tracking · Pay in AT with 7-Level Affiliate Network
                </p>
              </div>
              <div className="flex-shrink-0 hidden sm:flex items-center gap-1 text-indigo-300 group-hover:translate-x-1 transition">
                <span className="text-sm font-semibold">Open</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </Link>

        {/* AVX Mini Widget — Live trading snapshot */}
        {!isHidden('avx') && <AVXMiniWidget />}

        {/* AVX Today's P&L — Realtime daily earnings */}
        {!isHidden('avx') && <AVXTodayPnLWidget />}

        {/* TSB Rewards Wallet — Closed-loop loyalty */}
        {!isHidden('tsb') && <TSBMiniWidget />}

        {/* Display Windows / Video Section */}
        {displayWindows.length > 0 && (
          <div className="mb-12" data-testid="display-windows-section">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Play className="w-6 h-6 text-primary" />
              Featured Content
            </h2>
            <div className={`grid gap-6 ${displayWindows.length === 1 ? 'max-w-3xl' : 'md:grid-cols-2'}`}>
              {displayWindows.map((window) => (
                <div key={window.id} className="glass-card overflow-hidden" data-testid={`display-window-${window.id}`}>
                  <div className="aspect-video bg-black">
                    <iframe
                      src={getEmbedUrl(window.video_url)}
                      title={window.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg">{window.title}</h3>
                    {window.description && (
                      <p className="text-sm text-muted-foreground mt-1">{window.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="glass-card p-6" data-testid="stat-orders">
            <ShoppingBag className="w-8 h-8 text-primary mb-3" />
            <p className="text-3xl font-bold mb-1">{stats.totalOrders}</p>
            <p className="text-muted-foreground">Total Orders</p>
          </div>
          
          <div className="glass-card p-6" data-testid="stat-spent">
            <Package className="w-8 h-8 text-secondary mb-3" />
            <p className="text-3xl font-bold mb-1">₹{stats.totalSpent.toFixed(2)}</p>
            <p className="text-muted-foreground">Total Spent</p>
          </div>
          
          <div className="glass-card p-6" data-testid="stat-cash-wallet">
            <Wallet className="w-8 h-8 text-primary mb-3" />
            <p className="text-3xl font-bold mb-1">₹{user?.cash_wallet?.toFixed(2) || '0.00'}</p>
            <p className="text-muted-foreground">Cash Wallet</p>
          </div>
          
          <div className="glass-card p-6" data-testid="stat-commission-wallet">
            <Wallet className="w-8 h-8 text-accent mb-3" />
            <p className="text-3xl font-bold mb-1">₹{user?.commission_wallet?.toFixed(2) || '0.00'}</p>
            <p className="text-muted-foreground">Commission Wallet</p>
          </div>
        </div>

        {/* Commission Dashboard - Expandable Section */}
        <div className="mb-8" data-testid="commission-section">
          <button 
            onClick={() => setShowCommissionDashboard(!showCommissionDashboard)}
            className="w-full glass-card p-4 border-2 border-green-500/30 hover:border-green-500/50 transition-all flex items-center justify-between group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-green-500/20">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-lg group-hover:text-green-500 transition-colors">
                  Commission Dashboard
                </h3>
                <p className="text-sm text-muted-foreground">
                  View detailed earnings breakdown by level
                </p>
              </div>
            </div>
            {showCommissionDashboard ? (
              <ChevronUp className="w-6 h-6 text-green-500" />
            ) : (
              <ChevronDown className="w-6 h-6 text-muted-foreground group-hover:text-green-500" />
            )}
          </button>
          
          {showCommissionDashboard && (
            <div className="mt-4 animate-in slide-in-from-top duration-300">
              <CommissionDashboard />
            </div>
          )}
        </div>

        {/* Quick Transfer Section */}
        <div className="mb-8">
          <QuickTransfer />
        </div>

        {/* System Guide Section */}
        <div className="mb-8 glass-card p-6 border-2 border-secondary/30 bg-gradient-to-r from-secondary/5 to-primary/5" data-testid="system-guide-section">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-secondary/20 rounded-xl">
                <BookOpen className="w-8 h-8 text-secondary" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-1">📚 Complete Tutorials · मास्टर मार्गदर्शिका</h2>
                <p className="text-muted-foreground text-sm">
                  Bilingual (English + हिन्दी) · All features covered · Updated v2026.1
                </p>
              </div>
            </div>
            {/* NEW: Complete Bilingual Tutorial 2026 — flagship link */}
            <a
              href="/ANANT_COMPLETE_TUTORIAL_2026.html"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-fuchsia-500 via-purple-500 to-amber-500 text-white font-black hover:brightness-110 transition-all shadow-lg border-2 border-amber-300/50"
              data-testid="download-complete-tutorial-btn"
            >
              <div className="text-3xl">🌟</div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest opacity-90 font-black">NEW · v2026.1</div>
                <div className="text-base font-black" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}>Complete Master Tutorial (Bilingual)</div>
                <div className="text-xs opacity-95">All 13 modules · English + हिन्दी · Print-to-PDF ready</div>
              </div>
              <Download className="w-6 h-6 opacity-90 group-hover:scale-110 transition-transform" />
            </a>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <a
                href="/Member_Guide.pdf"
                download="ANANT_Member_Success_Guide.pdf"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary/20 to-secondary/20 text-primary rounded-xl hover:from-primary/30 hover:to-secondary/30 transition-colors font-medium border border-primary/30"
                data-testid="download-member-guide-btn"
              >
                <Download className="w-5 h-5" />
                <span className="text-sm">Member Guide (Affiliate Network Tips)</span>
              </a>
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sankat-mochan/system-guide/download`);
                    if (res.ok) {
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'ANANT_System_Guide.pdf';
                      a.click();
                      window.URL.revokeObjectURL(url);
                    }
                  } catch (error) {
                    console.error('Download failed:', error);
                  }
                }}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-secondary/20 text-secondary rounded-xl hover:bg-secondary/30 transition-colors font-medium"
                data-testid="download-guide-btn"
              >
                <Download className="w-5 h-5" />
                <span className="text-sm">System Guide</span>
              </button>
              <Link
                to="/audio-guide"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-500/20 text-purple-400 rounded-xl hover:bg-purple-500/30 transition-colors font-medium"
                data-testid="audio-guide-btn"
              >
                <Headphones className="w-5 h-5" />
                <span className="text-sm">Audio Guide</span>
              </Link>
            </div>
          </div>
        </div>

        {/* AI Creator Tools Section */}
        <div className="mb-8" data-testid="ai-tools-section">
          <div className="flex items-center gap-2 mb-4">
            <Wand2 className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold">AI Creator Tools</h2>
            <span className="text-[10px] font-bold bg-gradient-to-r from-primary to-purple-500 text-white px-2 py-0.5 rounded-full animate-pulse">
              NEW
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/tools/virtual-tour"
              className="glass-card p-4 hover:border-cyan-500/50 transition-all group relative overflow-hidden"
              data-testid="ai-virtual-tour-card"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-cyan-500/20 to-transparent rounded-bl-full"></div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold group-hover:text-cyan-500 transition-colors">Virtual Tour Creator</h3>
                    <Sparkles className="w-4 h-4 text-cyan-500" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Create 360° tours from photos with AI
                  </p>
                </div>
              </div>
            </Link>
            <Link
              to="/tools/video-ad"
              className="glass-card p-4 hover:border-purple-500/50 transition-all group relative overflow-hidden"
              data-testid="ai-video-ad-card"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-500/20 to-transparent rounded-bl-full"></div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center flex-shrink-0">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold group-hover:text-purple-500 transition-colors">AI Video Ad Creator</h3>
                    <Sparkles className="w-4 h-4 text-purple-500" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Transform photos into video ads
                  </p>
                </div>
              </div>
            </Link>
            <Link
              to="/algotrading"
              className="glass-card p-4 hover:border-pink-500/50 transition-all group relative overflow-hidden"
              data-testid="user-card-algotrading"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-500/20 to-transparent rounded-bl-full"></div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold group-hover:text-pink-500 transition-colors">Anant Smart Trading</h3>
                    <span className="text-[10px] font-bold bg-gradient-to-r from-pink-500 to-rose-500 text-white px-1.5 py-0.5 rounded-full">LIVE</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Stock + Crypto bots · 11 brokers · F&O Pro
                  </p>
                </div>
              </div>
            </Link>
            {user?.role === 'admin' && (
              <Link
                to="/admin/displayengine"
                className="glass-card p-4 hover:border-amber-500/50 transition-all group relative overflow-hidden"
                data-testid="user-card-displayengine"
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-500/20 to-transparent rounded-bl-full"></div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold group-hover:text-amber-500 transition-colors">Display Engine</h3>
                      <span className="text-[10px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full">ADMIN</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Digital signage · 6 templates · auto-translate
                    </p>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>

        {/* Lead Dashboard Card */}
        <div className="mb-8" data-testid="lead-dashboard-section">
          <Link
            to="/my-leads"
            className="glass-card p-5 hover:border-green-500/50 transition-all group flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold group-hover:text-green-500 transition-colors">My Leads Dashboard</h3>
                <p className="text-sm text-muted-foreground">
                  Manage property inquiries & referral leads
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-green-500">
              <span className="text-sm font-medium">View Leads</span>
              <ArrowRight className="w-5 h-5" />
            </div>
          </Link>
        </div>

        {/* Share Earnings Counter - NEW */}
        <div className="mb-8">
          <ShareEarningsCounter />
        </div>

        {/* My Unlocked Products Section */}
        <div className="mb-8">
          <UnlockedProducts />
        </div>

        {/* Share Referral Section */}
        <div className="mb-8 glass-card p-6 border-2 border-primary/30" data-testid="share-referral-section">
          <div className="flex items-center gap-3 mb-4">
            <Share2 className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">Share & Earn</h2>
          </div>
          <p className="text-muted-foreground mb-4">
            Share your referral link with friends and earn commissions on every signup!
          </p>
          <ShareButton
            type="page"
            itemName="ANANT WEB PORTAL"
            variant="full"
          />
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
          <Link
            to="/games"
            className="glass-card p-6 hover:border-purple-500 transition-all group border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-pink-500/10 relative overflow-hidden"
            data-testid="link-games"
          >
            <div className="absolute top-2 right-2 bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
              NEW
            </div>
            <Gamepad2 className="w-8 h-8 text-purple-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">🎮 Games</h3>
            <p className="text-muted-foreground text-sm">Play & Earn AGP</p>
          </Link>

          <Link
            to="/agp-wallet"
            className="glass-card p-6 hover:border-yellow-500 transition-all group border-2 border-yellow-500/30"
            data-testid="link-agp-wallet"
          >
            <Coins className="w-8 h-8 text-yellow-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">AGP Wallet</h3>
            <p className="text-muted-foreground text-sm">Gaming Points</p>
          </Link>

          <Link
            to="/wallets"
            className="glass-card p-6 hover:border-green-500 transition-all group border-2 border-green-500/20"
            data-testid="link-wallets"
          >
            <Wallet className="w-8 h-8 text-green-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">My Wallets</h3>
            <p className="text-muted-foreground text-sm">3 Wallets + QR Pay</p>
          </Link>
          
          <Link
            to="/my-orders"
            className="glass-card p-6 hover:border-primary transition-all group"
            data-testid="link-orders"
          >
            <ShoppingBag className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">My Orders</h3>
            <p className="text-muted-foreground text-sm">View all orders</p>
          </Link>
          
          <Link
            to="/my-products"
            className="glass-card p-6 hover:border-primary transition-all group"
            data-testid="link-products"
          >
            <Package className="w-8 h-8 text-secondary mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">My Products</h3>
            <p className="text-muted-foreground text-sm">Access downloads</p>
          </Link>
          
          <Link
            to="/my-courses"
            className="glass-card p-6 hover:border-primary transition-all group"
            data-testid="link-courses"
          >
            <BookOpen className="w-8 h-8 text-accent mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">My Courses</h3>
            <p className="text-muted-foreground text-sm">Continue learning</p>
          </Link>
          
          <Link
            to="/profile"
            className="glass-card p-6 hover:border-primary transition-all group"
            data-testid="link-profile"
          >
            <User className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">Profile</h3>
            <p className="text-muted-foreground text-sm">Account settings</p>
          </Link>
          
          <Link
            to="/my-shops"
            className="glass-card p-6 hover:border-emerald-500 transition-all group border-2 border-emerald-500/20"
            data-testid="link-my-shops"
          >
            <Store className="w-8 h-8 text-emerald-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">My Shops</h3>
            <p className="text-muted-foreground text-sm">Create & manage shops</p>
          </Link>
          
          <Link
            to="/my-magic-qr"
            className="glass-card p-6 hover:border-amber-500 transition-all group border-2 border-amber-500/20"
            data-testid="link-magic-qr"
          >
            <QrCode className="w-8 h-8 text-amber-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">Magic QR</h3>
            <p className="text-muted-foreground text-sm">Smart review collection</p>
          </Link>
        </div>

        {/* Affiliate Network Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <Link
            to="/commissions"
            className="glass-card p-6 hover:border-green-500 transition-all group border-2 border-green-500/20"
            data-testid="link-commissions"
          >
            <TrendingUp className="w-8 h-8 text-green-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">Commissions</h3>
            <p className="text-muted-foreground text-sm">Affiliate Network Earnings & Level Status</p>
          </Link>
          
          <Link
            to="/matrix"
            className="glass-card p-6 hover:border-purple-500 transition-all group border-2 border-purple-500/20"
            data-testid="link-matrix"
          >
            <GitBranch className="w-8 h-8 text-purple-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">My Matrix</h3>
            <p className="text-muted-foreground text-sm">10x10 Team Tree View</p>
          </Link>
          
          <Link
            to="/my-ranks"
            className="glass-card p-6 hover:border-yellow-500 transition-all group border-2 border-yellow-500/20"
            data-testid="link-my-ranks"
          >
            <Trophy className="w-8 h-8 text-yellow-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">Ranks & Rewards</h3>
            <p className="text-muted-foreground text-sm">Progress, rewards & offers</p>
          </Link>
          
          <Link
            to="/affiliate"
            className="glass-card p-6 hover:border-pink-500 transition-all group border-2 border-pink-500/20"
            data-testid="link-affiliate"
          >
            <Share2 className="w-8 h-8 text-pink-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">Affiliate</h3>
            <p className="text-muted-foreground text-sm">Promote & earn commissions</p>
          </Link>
          
          <Link
            to="/team-report"
            className="glass-card p-6 hover:border-blue-500 transition-all group border-2 border-blue-500/20"
            data-testid="link-team-report"
          >
            <Users className="w-8 h-8 text-blue-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">Team Report</h3>
            <p className="text-muted-foreground text-sm">Full member list with contacts</p>
          </Link>
          
          <Link
            to="/notifications"
            className="glass-card p-6 hover:border-orange-500 transition-all group border-2 border-orange-500/20"
            data-testid="link-notifications"
          >
            <Bell className="w-8 h-8 text-orange-500 mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">Notifications</h3>
            <p className="text-muted-foreground text-sm">Alerts & push settings</p>
          </Link>
          
          <Link
            to="/transactions"
            className="glass-card p-6 hover:border-primary transition-all group"
            data-testid="link-transactions"
          >
            <History className="w-8 h-8 text-secondary mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-lg">Transactions</h3>
            <p className="text-muted-foreground text-sm">View history</p>
          </Link>
        </div>

        {/* Important Links Section - Header Position */}
        {headerLinks.length > 0 && (
          <div className="mt-6" data-testid="important-links-section">
            <h2 className="text-xl font-bold mb-4 text-muted-foreground flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" />
              Important Links
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {headerLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-card p-6 hover:border-primary transition-all group border-2 border-primary/20"
                  data-testid={`important-link-${link.id}`}
                >
                  <ExternalLink className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
                  <h3 className="font-semibold text-lg">{link.title}</h3>
                  <p className="text-muted-foreground text-sm">Open in new tab</p>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Tools Section */}
        <div className="mt-6">
          <h2 className="text-xl font-bold mb-4 text-muted-foreground">Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <Link
              to="/my-card"
              className="glass-card p-6 hover:border-cyan-500 transition-all group border-2 border-cyan-500/20"
              data-testid="link-dvc"
            >
              <IdCard className="w-8 h-8 text-cyan-500 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-lg">My Digital Card</h3>
              <p className="text-muted-foreground text-sm">Create & Share Your DVC</p>
            </Link>
            
            <Link
              to="/my-link-bio"
              className="glass-card p-6 hover:border-green-500 transition-all group border-2 border-green-500/30 bg-gradient-to-br from-green-500/5 to-transparent"
              data-testid="link-bio"
            >
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-8 h-8 text-green-500 group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full">FREE</span>
              </div>
              <h3 className="font-semibold text-lg">Link in Bio</h3>
              <p className="text-muted-foreground text-sm">One link for all your socials</p>
            </Link>
            
            <Link
              to="/biodata-builder"
              className="glass-card p-6 hover:border-yellow-500 transition-all group border-2 border-yellow-500/30 bg-gradient-to-br from-yellow-500/5 to-transparent"
              data-testid="link-biodata"
            >
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-8 h-8 text-yellow-500 group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded-full">NEW</span>
              </div>
              <h3 className="font-semibold text-lg">Bio Data Builder</h3>
              <p className="text-muted-foreground text-sm">Create marriage biodatas</p>
            </Link>
            
            <Link
              to="/helpdesk"
              className="glass-card p-6 hover:border-green-500 transition-all group border-2 border-green-500/50 bg-gradient-to-br from-green-500/10 to-transparent"
              data-testid="link-helpdesk"
            >
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-8 h-8 text-green-500 group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold bg-green-500 text-white px-2 py-0.5 rounded-full">100% FREE</span>
              </div>
              <h3 className="font-semibold text-lg">Helpdesk Support</h3>
              <p className="text-muted-foreground text-sm">Free chat with support team</p>
            </Link>
            
            <Link
              to="/supertool"
              className="glass-card p-6 hover:border-purple-500 transition-all group border-2 border-purple-500/50 bg-gradient-to-br from-purple-500/10 to-transparent"
              data-testid="link-supertool"
            >
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="w-8 h-8 text-purple-500 group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold bg-purple-500 text-white px-2 py-0.5 rounded-full">NEW</span>
              </div>
              <h3 className="font-semibold text-lg">SuperTool</h3>
              <p className="text-muted-foreground text-sm">Account Book + Calendar</p>
            </Link>
            
            <Link
              to="/qr-leads"
              className="glass-card p-6 hover:border-green-500 transition-all group border-2 border-green-500/50 bg-gradient-to-br from-green-500/10 to-transparent"
              data-testid="link-qr-leads"
            >
              <div className="flex items-center gap-2 mb-3">
                <QrCode className="w-8 h-8 text-green-500 group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold bg-green-500 text-white px-2 py-0.5 rounded-full">NEW</span>
              </div>
              <h3 className="font-semibold text-lg">QR Lead Collector</h3>
              <p className="text-muted-foreground text-sm">WhatsApp numbers collect करें</p>
            </Link>
          </div>
        </div>

        {/* Marketplace & Auctions Section */}
        <div className="mt-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Gavel className="w-5 h-5 text-accent" />
            Marketplace & Auctions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Link
              to="/avx"
              className="glass-card p-6 hover:border-yellow-400 transition-all group border-2 border-yellow-500/60 bg-gradient-to-br from-yellow-500/15 via-orange-500/10 to-purple-500/10 relative overflow-hidden"
              data-testid="link-avx"
            >
              <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                <span className="text-[10px] font-bold bg-yellow-500 text-black px-2 py-0.5 rounded-full">FANTASY</span>
                <span className="text-[10px] font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span> LIVE
                </span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-8 h-8 text-yellow-400 group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="font-bold text-lg bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">AVX Exchange</h3>
              <p className="text-muted-foreground text-sm">Trade BTC, ETH, Gold + Earn 33% Affiliate Network Commission</p>
              <div className="mt-2 text-xs text-yellow-300 font-semibold flex items-center gap-1">
                Open Trading <ArrowRight className="w-3 h-3" />
              </div>
            </Link>

            <Link
              to="/deals"
              className="glass-card p-6 hover:border-green-500 transition-all group border-2 border-green-500/30 bg-gradient-to-br from-green-500/5 to-transparent"
              data-testid="link-p2p-deals"
            >
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-8 h-8 text-green-500 group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full">NEW</span>
              </div>
              <h3 className="font-semibold text-lg">P2P Deals</h3>
              <p className="text-muted-foreground text-sm">Buy & sell with members</p>
            </Link>
            
            <Link
              to="/auctions"
              className="glass-card p-6 hover:border-red-500 transition-all group border-2 border-red-500/30 bg-gradient-to-br from-red-500/5 to-transparent"
              data-testid="link-auction-house"
            >
              <div className="flex items-center gap-2 mb-3">
                <Gavel className="w-8 h-8 text-red-500 group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold bg-red-500/20 text-red-500 px-2 py-0.5 rounded-full animate-pulse">LIVE</span>
              </div>
              <h3 className="font-semibold text-lg">Auction House</h3>
              <p className="text-muted-foreground text-sm">Live bidding on exclusive items</p>
            </Link>
            
            <Link
              to="/deals/buy-pass"
              className="glass-card p-6 hover:border-yellow-500 transition-all group border-2 border-yellow-500/20"
              data-testid="link-buy-pass"
            >
              <CreditCard className="w-8 h-8 text-yellow-500 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-lg">Buy Passes</h3>
              <p className="text-muted-foreground text-sm">Get listing passes</p>
            </Link>
            
            <Link
              to="/my-passes"
              className="glass-card p-6 hover:border-cyan-500 transition-all group border-2 border-cyan-500/20"
              data-testid="link-my-passes"
            >
              <Ticket className="w-8 h-8 text-cyan-500 mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-lg">My Passes</h3>
              <p className="text-muted-foreground text-sm">View pass inventory</p>
            </Link>
            
            <Link
              to="/franchise"
              className="glass-card p-6 hover:border-purple-500 transition-all group border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-transparent"
              data-testid="link-franchise"
            >
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-8 h-8 text-purple-500 group-hover:scale-110 transition-transform" />
                <span className="text-[10px] font-bold bg-purple-500/20 text-purple-500 px-2 py-0.5 rounded-full">CAPTAIN</span>
              </div>
              <h3 className="font-semibold text-lg">Franchise Zone</h3>
              <p className="text-muted-foreground text-sm">Become a City/District Captain</p>
            </Link>
          </div>
        </div>

        {/* Free Tools Section */}
        <div className="mt-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-green-500" />
            Free Business Tools
            <span className="text-[10px] font-bold bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full">FREE</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/tools/whatsapp-link"
              className="glass-card p-4 hover:border-green-500 transition-all group"
              data-testid="link-whatsapp-tool"
            >
              <MessageCircle className="w-6 h-6 text-green-500 mb-2 group-hover:scale-110 transition-transform" />
              <h3 className="font-medium text-sm">WhatsApp Link</h3>
            </Link>
            <Link
              to="/tools/invoice"
              className="glass-card p-4 hover:border-blue-500 transition-all group"
              data-testid="link-invoice-tool"
            >
              <FileText className="w-6 h-6 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
              <h3 className="font-medium text-sm">Invoice Generator</h3>
            </Link>
            <Link
              to="/tools/testimonials"
              className="glass-card p-4 hover:border-yellow-500 transition-all group"
              data-testid="link-testimonial-tool"
            >
              <Star className="w-6 h-6 text-yellow-500 mb-2 group-hover:scale-110 transition-transform" />
              <h3 className="font-medium text-sm">Testimonials</h3>
            </Link>
            <Link
              to="/tools/qr-code"
              className="glass-card p-4 hover:border-purple-500 transition-all group"
              data-testid="link-qr-tool"
            >
              <QrCode className="w-6 h-6 text-purple-500 mb-2 group-hover:scale-110 transition-transform" />
              <h3 className="font-medium text-sm">QR Code</h3>
            </Link>
          </div>
        </div>

        {/* Custom Tabs from Admin */}
        <CustomDashboardTabs />

        {/* Recent Orders */}
        {orders.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">Recent Orders</h2>
            <div className="space-y-4">
              {orders.slice(0, 5).map((order) => (
                <Link
                  key={order.id}
                  to={`/my-orders`}
                  className="glass-card p-6 flex justify-between items-center hover:border-primary transition-all"
                  data-testid={`recent-order-${order.id}`}
                >
                  <div>
                    <p className="font-semibold mb-1">Order #{order.id.slice(0, 8)}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString()} • {order.items.length} items
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-primary">₹{order.total_amount.toFixed(2)}</p>
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      order.payment_status === 'completed'
                        ? 'bg-accent/20 text-accent'
                        : 'bg-muted/20 text-muted-foreground'
                    }`}>
                      {order.payment_status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Community & Leaderboard Section */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <CommunityStats />
          <ReferralLeaderboard />
        </div>
      </div>

      {/* Attraction Popup */}
      <AttractionPopup page="dashboard" />

      {/* Buy AT Balance modal */}
      <BuyATBalanceModal open={showBuyAT} onClose={() => setShowBuyAT(false)} />
    </div>
  );
};

export default UserDashboard;
